import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { StageController } from "../../Components/StageController";
import { WinAnimator } from "../WinAnimator";
import { FreespinsController } from "./FreespinsController";
import { GameManager } from "../../Managers/GameManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { DataManager } from "../../Managers/DataManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { WinAnimator2FS } from "../../CustomComponents/WinAnimator2FS";
import { AmbientBackground } from "../../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class FreespinsView extends ContainerElement {
    private _controller: FreespinsController;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: Machine;
    private _winAnimator: WinAnimator2FS;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;

    private _backgroundWin: SwitchElement;
    private _rays: SwitchElement;

    private _fsMessageStart: HTMLElement;
    private _fsMessageEnd: HTMLElement;
    private _fsMessageEndNoWin: HTMLElement;

    private shockWave1: PIXI.filters.ShockwaveFilter;
    private shockWave2: PIXI.filters.ShockwaveFilter;
    private shockWave3: PIXI.filters.ShockwaveFilter;
    private shockWave4: PIXI.filters.ShockwaveFilter;
    private shockWave5: PIXI.filters.ShockwaveFilter;

    private _introDoubleWildsDelay: FrameTimerEventEmitter;
    private _doubleWildMoved: boolean;

    private sceneWidth: number;
    private sceneHeight: number;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._introDoubleWildsDelay = CreateFrameTimer("_introDoubleWildsDelay");
        this._introDoubleWildsDelay.on(FrameTimerEvents.Complete, this.introDoubleWilds.bind(this));

        StageController.freespinsStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (node.hasAttribute("Width")) {
            this.sceneWidth = parseInt(node.getAttribute("Width"));
        }

        if (node.hasAttribute("Height")) {
            this.sceneHeight = parseInt(node.getAttribute("Height"));
        }

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                    this._paylines.reset();
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator2FS) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                }

                if (key === "BackgroundWin" && this.childs[key] instanceof SwitchElement) {
                    this._backgroundWin = this.childs[key];
                }

                if (key === "Rays" && this.childs[key] instanceof SwitchElement) {
                    this._rays = this.childs[key];
                }
            }

            this._fsMessageStart = document.getElementById("fsMessageStart");
            this._fsMessageEnd = document.getElementById("fsMessageEnd");
            this._fsMessageEndNoWin = document.getElementById("fsMessageEndNoWin");

            //prevent first animation on page load
            this._fsMessageStart.classList.add("_noAnim");
            this._fsMessageEnd.classList.add("_noAnim");
            this._fsMessageEndNoWin.classList.add("_noAnim");

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }

                if (this._fsMessageStart == null) {
                    this.log("ERROR no fsMessageStart");
                }

                if (this._fsMessageEnd == null) {
                    this.log("ERROR no fsMessageEnd");
                }

                if (this._fsMessageEndNoWin == null) {
                    this.log("ERROR no fsMessageEndNoWin");
                }
            }
        }

        this.shocks();
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: FreespinsController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.freespinsResponse != null) {
                    if (
                        GameManager.freespinsResponse.initData != null &&
                        GameManager.freespinsResponse.initData.reels !== null
                    ) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.on(Machine.SpinStopStart, this.machineSpinStopStart.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.freespinsResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                        );
                    }

                    if (this._winAnimator != null) {
                        this._winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                        this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                        this._winAnimator.on(
                            WinAnimator.FinishQuickWinCalculation,
                            this.finishQuickWinCalculation.bind(this),
                        );
                        this._winAnimator.initialize(
                            this._symbols,
                            this._paylines,
                            this._noWinContainer,
                            this._winContainer,
                        );
                        this._winAnimator.initializeDoubleWilds(this.childs["DoubleWilds"]);
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no freespin reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;
    }

    initIntro(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        this.allSwitchElementToCase("INIT_INTRO");

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.moveDoubleWilds(GameManager.freespinsResponse.customData, false);
        this._winAnimator.showInitWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin);

        this.visible = true;

        AmbientBackground.changeAmbientBackground("color-1", 0);

        this.retrigger();

        if (GameManager.freespinsResponse.currentWin > 0) {
            if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "NORMAL") {
                this._backgroundWin.goToCase("WIN");
            }
            if (this._rays != null && this._rays.getCurrentCase() === "NORMAL") {
                this._rays.goToCase("WIN");
            }
        }

        if (this.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");
            PlayList.loop("SND_FSWait");
            PlayList.play("SND_FSEnd");

            if (GameManager.freespinsResponse.totalWin > 0) {
                if (this._fsMessageEnd != null && this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
                    this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEnd.classList.remove("_noAnim");
                }
            } else {
                if (
                    this._fsMessageEndNoWin != null &&
                    this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEndNoWin.classList.remove("_noAnim");
                }
            }
        } else {
            PlayList.loop("SND_FSLoop");
        }
    }

    spin(): void {
        this._doubleWildMoved = false;

        if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "WIN") {
            this._backgroundWin.goToCase("WIN_TO_NORMAL");
        }
        if (this._rays != null && this._rays.getCurrentCase() === "WIN") {
            this._rays.goToCase("WIN_TO_NORMAL");
        }
        StageController.baseStage = false;
        StageController.freespinStage = true;

        PlayList.loop("SND_FSLoop");

        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }

        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    startReceived(): void {
        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }

        PlayList.stop("SND_FSWait");
        PlayList.stop("SND_FSWaitIn");

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.reset(true);
        this._winAnimator.hideInitDoubleWildPositionSymbol(GameManager.freespinsResponse.customData);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    endReceived(): void {}

    onReelStopped(): void {
        if (this._machine.actualReelStopped == 0) {
            this.stopShockWaves();
        }

        this._winAnimator.onReelStopped(GameManager.freespinsResponse.screen.reels, this._machine.actualReelStopped);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(): void {
        if (GameManager.freespinsResponse.currentWin > 0) {
            if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "NORMAL") {
                this._backgroundWin.goToCase("WIN");
            }
            if (this._rays != null && this._rays.getCurrentCase() === "NORMAL") {
                this._rays.goToCase("WIN");
            }
        }
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin)) {
            PlayList.stop("SND_FSLoop");
        }

        if (GameManager.freespinsResponse.currentWin === 0) {
            if (this.isLastFreespin()) {
                PlayList.stop("SND_FSLoop");
                PlayList.loop("SND_FSWait");
                PlayList.play("SND_FSEnd");

                if (GameManager.freespinsResponse.totalWin > 0) {
                    if (
                        this._fsMessageEnd != null &&
                        this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEnd.classList.remove("_noAnim");
                    }
                } else {
                    if (
                        this._fsMessageEndNoWin != null &&
                        this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEndNoWin.classList.remove("_noAnim");
                    }
                }
            }
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        this._controller.allWinsFinished();

        if (this.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");
            PlayList.loop("SND_FSWait");
            PlayList.play("SND_FSEnd");

            if (GameManager.freespinsResponse.totalWin > 0) {
                if (this._fsMessageEnd != null && this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
                    this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEnd.classList.remove("_noAnim");
                }
            } else {
                if (
                    this._fsMessageEndNoWin != null &&
                    this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEndNoWin.classList.remove("_noAnim");
                }
            }
        } else {
            PlayList.loop("SND_FSLoop");
        }

        this.retrigger();
    }

    intro(long: boolean = false): void {
        StageController.freespinStage = true;

        this.visible = true;

        AmbientBackground.changeAmbientBackground("color-1", 100);

        this.childs["Paylines"].alpha = 0;
        this.childs["WinContainer"].alpha = 0;
        this.childs["WinAnimator"].alpha = 0;

        if (this.childs["Logo"]) {
            this.childs["Logo"].alpha = 0;
        }

        this.allSwitchElementToCase("INTRO");

        this._introDoubleWildsDelay.start(30);
    }

    introDoubleWilds(): void {
        this.setupInitDoubleWild();
        this.allSwitchElementToCase("INTRO_2");

        Tweens.customTween(this.childs["Paylines"], {
            tween: "alpha",
            tweenValues: [0, 1],
            tweenDuration: [60],
            tweenDelay: [90],
            tweenEase: ["linear"],
        });

        Tweens.customTween(this.childs["WinContainer"], {
            tween: "alpha",
            tweenValues: [0, 1],
            tweenDuration: [60],
            tweenDelay: [120],
            tweenEase: ["linear"],
        });

        Tweens.customTween(this.childs["WinAnimator"], {
            tween: "alpha",
            tweenValues: [0, 1],
            tweenDuration: [60],
            tweenDelay: [120],
            tweenEase: ["linear"],
        });

        if (this.childs["Logo"]) {
            Tweens.customTween(this.childs["Logo"], {
                tween: "alpha",
                tweenValues: [0, 1],
                tweenDuration: [60],
                tweenDelay: [90],
                tweenEase: ["linear"],
            });
        }

        this._introDelay.start(150);
    }

    private setupInitDoubleWild(): void {
        let wilds = <SwitchElement>this.childs["DoubleWilds"];
        this.shock1(this.sceneWidth * 0.5, this.sceneHeight * 0.5, 120, true);

        if (GameManager.freespinsResponse.customData && GameManager.freespinsResponse.customData["positions"]) {
            let positions: Array<Object> = GameManager.freespinsResponse.customData["positions"];
            let wild = <SwitchElement>wilds.getChildElementByName("Wild0");

            if (positions.length > 0) {
                wild.position.x = positions[0]["reelIndex"] * this._winAnimator.getSymbolWidth();
                wild.position.y = positions[0]["symbolIndex"] * this._winAnimator.getSymbolHeight();
            }
        }

        wilds.goToCase("INTRO_2");
    }

    introFinished(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        this.visible = true;

        PlayList.loop("SND_FSLoop");

        this._controller.introFinished();
    }

    outro(): void {
        StageController.freespinStage = true;

        this.visible = true;

        AmbientBackground.changeAmbientBackground("base", 100, 100);

        PlayList.stop("SND_FSLoop");
        PlayList.stop("SND_FSWait");
        PlayList.play("SND_Transition");

        if (this._fsMessageEnd != null && !this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageEnd.classList.add(HtmlController.DisabledClass);
        }

        if (
            this._fsMessageEndNoWin != null &&
            !this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
        ) {
            this._fsMessageEndNoWin.classList.add(HtmlController.DisabledClass);
        }

        <SwitchElement>this.childs["ReelsSeparator"].goToCase("OUTRO");
        <SwitchElement>this.childs["SceneTransition"].goToCase("OUTRO");

        if (this.childs["Logo"]) {
            Tweens.customTween(this.childs["Logo"], {
                tween: "alpha",
                tweenValues: [1, 0],
                tweenDuration: [30],
                tweenEase: ["linear"],
            });
        }

        this._outroDelay.start(30);
    }

    outroFinished(): void {
        this.stopShockWaves();

        StageController.freespinStage = false;

        this.visible = false;

        this.allSwitchElementToCase("EMPTY");

        this._winAnimator.reset(true);

        this._controller.outroFinished();
    }

    enable(): void {
        PlayList.play("SND_FSWin");
        PlayList.loop("SND_FSWaitIn");
        PlayList.stop("SND_BaseRecapLoop");

        if (this._fsMessageStart != null && this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
            this._fsMessageStart.classList.remove("_noAnim");
        }
    }

    private retrigger(): void {
        if (!this.isLastFreespin()) {
            if (GameManager.freespinsResponse.wins != null && GameManager.freespinsResponse.wins.length > 0) {
                for (let i: number = 0; i < GameManager.freespinsResponse.wins.length; i++) {
                    if (GameManager.freespinsResponse.wins[i].type === Communication.Game.WinType.freeSpins) {
                        if (
                            this._fsMessageStart != null &&
                            this._fsMessageStart.classList.contains(HtmlController.DisabledClass)
                        ) {
                            this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
                            this._fsMessageStart.classList.remove("_noAnim");
                        }
                    }
                }
            }
        }
    }

    private isLastFreespin(): boolean {
        if (DataManager.GameLimit) {
            return true;
        }

        return (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0
        );
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    private machineSpinStopStart(): void {
        if (!this._doubleWildMoved) {
            this._winAnimator.revealNewDoubleWild(GameManager.freespinsResponse.customData);
            this._winAnimator.moveDoubleWilds(GameManager.freespinsResponse.customData, true);
            this._doubleWildMoved = true;
        }
    }

    public shocks(): void {
        let options: ShockwaveOptions = new ShockwaveOptions();
        options.wavelength = this.sceneWidth * 0.2;
        options.amplitude = this.sceneHeight * 0.06;
        options.radius = this.sceneHeight * 0.7;
        options.brightness = 1;

        this.shockWave1 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave1.enabled = false;

        this.shockWave2 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave2.enabled = false;

        this.shockWave3 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave3.enabled = false;

        this.shockWave4 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave4.enabled = false;

        this.shockWave5 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave5.enabled = false;

        this.filters = [this.shockWave1, this.shockWave2, this.shockWave3, this.shockWave4, this.shockWave5];
    }

    public shock1(x: number, y: number, time: number = 160, isDoubleWild: boolean = false): void {
        this.shockWave1.center = [x, y];
        this.shockWave1.enabled = true;

        let progress = 1.5;
        this.shockWave1.wavelength = this.sceneWidth * 0.2;
        this.shockWave1.amplitude = this.sceneHeight * 0.06;
        this.shockWave1.radius = this.sceneHeight * 0.7;
        this.shockWave1.brightness = 1;

        if (isDoubleWild) {
            progress = 3;

            this.shockWave1.wavelength = this.sceneWidth * 0.1;
            this.shockWave1.amplitude = this.sceneHeight * 0.025;
            this.shockWave1.radius = this.sceneWidth * 1.05;
            this.shockWave1.brightness = 1.3;
        }

        Tweens.customTween(this, {
            tween: "shockWave1.time",
            tweenValues: [0, progress],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        if (!isDoubleWild) {
            Tweens.customTween(this, {
                tween: "shockWave1.amplitude",
                tweenValues: [this.sceneHeight * 0.06, 0],
                tweenDuration: [time],
                tweenEase: ["linear"],
                tweenLoop: false,
            });
        }
    }

    public shock2(x: number, y: number, time: number = 160): void {
        this.shockWave2.center = [x, y];
        this.shockWave2.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave2.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave2.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    public shock3(x: number, y: number, time: number = 160): void {
        this.shockWave3.center = [x, y];
        this.shockWave3.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave3.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave3.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    public shock4(x: number, y: number, time: number = 160): void {
        this.shockWave4.center = [x, y];
        this.shockWave4.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave4.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave4.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    public shock5(x: number, y: number, time: number = 160): void {
        this.shockWave5.center = [x, y];
        this.shockWave5.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave5.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave5.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    private stopShockWaves(): void {
        this.shockWave1.enabled = false;
        this.shockWave2.enabled = false;
        this.shockWave3.enabled = false;
        this.shockWave4.enabled = false;
        this.shockWave5.enabled = false;
    }
}

class ShockwaveOptions {
    amplitude?: number;
    wavelength?: number;
    brightness?: number;
    speed?: number;
    radius?: number;
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Freespins/FreespinsView.ts
 **/